import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import Field from '@/components/common/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import {
  CREATE_NOVEDAD_MUTATION,
  MONITOR_NOVEDADES_QUERY,
  NOVEDADES_BY_SUCURSAL_QUERY,
  NOVEDADES_MOTIVOS_QUERY,
  RESOLVER_NOVEDAD_MUTATION,
} from '@/graphql/novedades.gql.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import {
  CargasPage,
  CargasSection,
  CargasStickyActions,
  CargasTableShell,
  CargasViewport,
  cargasButtonClassName,
  cargasInputClassName,
  cargasPrimaryButtonClassName,
  useSaveShortcut,
} from './components/CargasLayout.jsx';

const defaultForm = {
  guiaId: '',
  motivoId: '',
  estado: 'no_entregado',
  detalle: '',
  evidenciaJson: '',
  fechaEvento: new Date().toISOString().slice(0, 16),
};

export default function NovedadesPage({ onlyRetenidas = false }) {
  const { sucursal } = useAuth();
  const sucursalId = Number(sucursal?.id || sucursal?.sucursal_id || 1);
  const [estadoFilter, setEstadoFilter] = useState(onlyRetenidas ? 'retenida' : '');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);

  const [{ data: motivosData }] = useQuery({ query: NOVEDADES_MOTIVOS_QUERY, variables: { activo: true } });
  const [{ data, fetching }, refetchNovedades] = useQuery({
    query: NOVEDADES_BY_SUCURSAL_QUERY,
    variables: { sucursalId, estado: onlyRetenidas ? 'retenida' : estadoFilter || null },
  });
  const [{ data: monitorData }] = useQuery({ query: MONITOR_NOVEDADES_QUERY, variables: { sucursalId } });
  const [, createNovedad] = useMutation(CREATE_NOVEDAD_MUTATION);
  const [, resolverNovedad] = useMutation(RESOLVER_NOVEDAD_MUTATION);

  const novedades = useMemo(() => data?.novedadesBySucursal || [], [data]);
  const motivos = motivosData?.novedadesMotivos || [];

  const onSubmit = async event => {
    event?.preventDefault?.();
    const payload = {
      ...form,
      sucursalId,
      guiaId: Number(form.guiaId),
      motivoId: Number(form.motivoId),
      fechaEvento: new Date(form.fechaEvento).toISOString(),
      evidenciaJson: form.evidenciaJson || null,
    };

    if (editing) {
      setEditing(null);
    } else {
      await createNovedad({ input: payload });
    }

    setShowForm(false);
    setForm(defaultForm);
    refetchNovedades({ requestPolicy: 'network-only' });
  };

  useSaveShortcut(onSubmit, showForm);

  const handleResolver = async novedadId => {
    await resolverNovedad({ novedadId, detalle: 'Resuelta desde tablero de novedades' });
    refetchNovedades({ requestPolicy: 'network-only' });
  };

  const monitorItems = [
    ['Pendientes', monitorData?.monitorNovedades?.pendientes || 0],
    ['Retenidas', monitorData?.monitorNovedades?.retenidas || 0],
    ['Reprogramadas', monitorData?.monitorNovedades?.reprogramadas || 0],
    ['Resueltas hoy', monitorData?.monitorNovedades?.resueltasHoy || 0],
  ];

  return (
    <CargasPage
      title={onlyRetenidas ? 'Novedades retenidas' : 'Novedades'}
      description="Monitor operativo por sucursal, linea y chofer."
      hideHeaderOnCompact
      footer={
        <CargasStickyActions
          summary={
            <div className="flex flex-wrap items-center gap-3 text-xs text-[rgba(var(--muted),0.92)]">
              <span>Sucursal: {sucursalId}</span>
              <span>Registros: {novedades.length}</span>
              {showForm ? <span>Ctrl + S para guardar</span> : null}
            </div>
          }
          actions={
            <>
              {!onlyRetenidas ? (
                <button
                  type="button"
                  className={cargasButtonClassName}
                  onClick={() => refetchNovedades({ requestPolicy: 'network-only' })}
                >
                  Actualizar
                </button>
              ) : null}
              <button
                type="button"
                className={showForm ? cargasButtonClassName : cargasPrimaryButtonClassName}
                onClick={() => setShowForm(prev => !prev)}
              >
                {showForm ? 'Cerrar formulario' : 'Registrar novedad'}
              </button>
            </>
          }
        />
      }
    >
      <CargasViewport className="grid grid-cols-12 gap-3">
        <div className="col-span-12 grid grid-cols-12 gap-3">
          {monitorItems.map(([label, value]) => (
            <CargasSection key={label} className="col-span-6 md:col-span-3" bodyClassName="p-3">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[rgba(var(--muted),0.9)]">{label}</p>
              <p className="mt-2 text-2xl font-semibold text-[rgb(var(--fg))]">{value}</p>
            </CargasSection>
          ))}
        </div>

        <CargasSection
          title="Gestion de novedades"
          className="col-span-12 md:col-span-8"
          bodyClassName="min-h-0 space-y-3 p-4"
        >
          {!onlyRetenidas ? (
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-12 md:col-span-4">
                <Field label="Estado" htmlFor="estadoFilter">
                  <select
                    id="estadoFilter"
                    className={cargasInputClassName}
                    value={estadoFilter}
                    onChange={event => setEstadoFilter(event.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="no_entregado">No entregada</option>
                    <option value="retenida">Retenida</option>
                    <option value="reprogramada">Reprogramada</option>
                    <option value="resuelta">Resuelta</option>
                  </select>
                </Field>
              </div>
              <div className="col-span-12 md:col-span-8 flex items-end gap-2">
                <button
                  type="button"
                  className={cargasButtonClassName}
                  onClick={() => refetchNovedades({ requestPolicy: 'network-only' })}
                >
                  Aplicar filtro
                </button>
              </div>
            </div>
          ) : null}

          {showForm ? (
            <form onSubmit={onSubmit} className="grid grid-cols-12 gap-2 rounded-xl border border-[rgba(var(--border),0.45)] p-3">
              <div className="col-span-12 md:col-span-3">
                <Field label="Guia" htmlFor="guiaId" required>
                  <input
                    id="guiaId"
                    value={form.guiaId}
                    onChange={e => setForm({ ...form, guiaId: e.target.value })}
                    className={cargasInputClassName}
                    required
                  />
                </Field>
              </div>
              <div className="col-span-12 md:col-span-3">
                <Field label="Motivo" htmlFor="motivoId" required>
                  <select
                    id="motivoId"
                    className={cargasInputClassName}
                    value={form.motivoId}
                    onChange={e => setForm({ ...form, motivoId: e.target.value })}
                    required
                  >
                    <option value="">Seleccionar</option>
                    {motivos.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.nombre}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-span-12 md:col-span-3">
                <Field label="Estado" htmlFor="estado" required>
                  <select
                    id="estado"
                    className={cargasInputClassName}
                    value={form.estado}
                    onChange={e => setForm({ ...form, estado: e.target.value })}
                  >
                    <option value="no_entregado">No entregada</option>
                    <option value="retenida">Retenida</option>
                    <option value="reprogramada">Reprogramada</option>
                  </select>
                </Field>
              </div>
              <div className="col-span-12 md:col-span-3">
                <Field label="Fecha" htmlFor="fechaEvento" required>
                  <input
                    id="fechaEvento"
                    type="datetime-local"
                    value={form.fechaEvento}
                    onChange={e => setForm({ ...form, fechaEvento: e.target.value })}
                    className={cargasInputClassName}
                    required
                  />
                </Field>
              </div>
              <div className="col-span-12">
                <Field label="Detalle" htmlFor="detalle">
                  <input
                    id="detalle"
                    value={form.detalle}
                    onChange={e => setForm({ ...form, detalle: e.target.value })}
                    className={cargasInputClassName}
                  />
                </Field>
              </div>
            </form>
          ) : null}

          {fetching ? <p className="text-sm text-[rgba(var(--muted),0.9)]">Cargando...</p> : null}
          {!fetching && novedades.length === 0 ? (
            <EmptyState title="Sin novedades" description="No hay novedades para los filtros actuales." />
          ) : null}
          {!fetching && novedades.length > 0 ? (
            <CargasTableShell className="max-h-[300px]">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-[rgb(var(--panel))] text-[rgba(var(--muted),0.9)]">
                  <tr>
                    <th className="px-3 py-2">Guia</th>
                    <th className="px-3 py-2">Reparto</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2">Motivo</th>
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Detalle</th>
                    <th className="px-3 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {novedades.map(item => (
                    <tr key={item.id} className="border-t border-[rgba(var(--border),0.45)]">
                      <td className="px-3 py-2">{item.guiaId}</td>
                      <td className="px-3 py-2">
                        {item.repartoId ? (
                          <Link className="text-[rgb(var(--brand))] hover:underline" to={`/repartos/${item.repartoId}`}>
                            #{item.repartoId}
                          </Link>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-2">{item.estado}</td>
                      <td className="px-3 py-2">{item.motivoId}</td>
                      <td className="px-3 py-2">{new Date(item.fechaEvento).toLocaleString('es-AR')}</td>
                      <td className="px-3 py-2">{item.detalle || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-2">
                          <button type="button" className={cargasButtonClassName} onClick={() => setEditing(item)}>
                            Editar
                          </button>
                          <button
                            type="button"
                            className={cargasPrimaryButtonClassName}
                            onClick={() => handleResolver(item.id)}
                          >
                            Resolver
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CargasTableShell>
          ) : null}
        </CargasSection>

        <CargasSection
          title="Contexto"
          className="col-span-12 md:col-span-4"
          bodyClassName="space-y-2 p-4 text-sm"
        >
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">Sucursal: {sucursalId}</div>
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">
            Retenidas: {monitorData?.monitorNovedades?.retenidas || 0}
          </div>
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">
            Pendientes: {monitorData?.monitorNovedades?.pendientes || 0}
          </div>
          <p className="text-xs text-[rgba(var(--muted),0.92)]">
            {editing ? 'La edicion detallada sigue pendiente; por ahora podes resolver o registrar nuevas novedades.' : 'Usa el filtro para concentrarte en el flujo operativo.'}
          </p>
        </CargasSection>
      </CargasViewport>
    </CargasPage>
  );
}
